/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";
import {
  getReputationComponentDecoder,
  getReputationComponentEncoder,
  type ReputationComponent,
  type ReputationComponentArgs,
} from "../types";

export const UPDATE_GHOST_SCORE_DISCRIMINATOR = new Uint8Array([
  213, 152, 5, 80, 163, 224, 151, 120,
]);

export function getUpdateGhostScoreDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_GHOST_SCORE_DISCRIMINATOR,
  );
}

export type UpdateGhostScoreInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateGhostScoreInstructionData = {
  discriminator: ReadonlyUint8Array;
  newScore: bigint;
  components: Option<Array<ReputationComponent>>;
};

export type UpdateGhostScoreInstructionDataArgs = {
  newScore: number | bigint;
  components: OptionOrNullable<Array<ReputationComponentArgs>>;
};

export function getUpdateGhostScoreInstructionDataEncoder(): Encoder<UpdateGhostScoreInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["newScore", getU64Encoder()],
      [
        "components",
        getOptionEncoder(getArrayEncoder(getReputationComponentEncoder())),
      ],
    ]),
    (value) => ({ ...value, discriminator: UPDATE_GHOST_SCORE_DISCRIMINATOR }),
  );
}

export function getUpdateGhostScoreInstructionDataDecoder(): Decoder<UpdateGhostScoreInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["newScore", getU64Decoder()],
    [
      "components",
      getOptionDecoder(getArrayDecoder(getReputationComponentDecoder())),
    ],
  ]);
}

export function getUpdateGhostScoreInstructionDataCodec(): Codec<
  UpdateGhostScoreInstructionDataArgs,
  UpdateGhostScoreInstructionData
> {
  return combineCodec(
    getUpdateGhostScoreInstructionDataEncoder(),
    getUpdateGhostScoreInstructionDataDecoder(),
  );
}

export type UpdateGhostScoreInput<
  TAccountAgentAccount extends string = string,
  TAccountAuthority extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  /** Authority allowed to update scores (could be governance/oracle) */
  authority: TransactionSigner<TAccountAuthority>;
  newScore: UpdateGhostScoreInstructionDataArgs["newScore"];
  components: UpdateGhostScoreInstructionDataArgs["components"];
};

export function getUpdateGhostScoreInstruction<
  TAccountAgentAccount extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateGhostScoreInput<TAccountAgentAccount, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): UpdateGhostScoreInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.authority),
    ],
    data: getUpdateGhostScoreInstructionDataEncoder().encode(
      args as UpdateGhostScoreInstructionDataArgs,
    ),
    programAddress,
  } as UpdateGhostScoreInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountAuthority
  >);
}

export type ParsedUpdateGhostScoreInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    agentAccount: TAccountMetas[0];
    /** Authority allowed to update scores (could be governance/oracle) */
    authority: TAccountMetas[1];
  };
  data: UpdateGhostScoreInstructionData;
};

export function parseUpdateGhostScoreInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateGhostScoreInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { agentAccount: getNextAccount(), authority: getNextAccount() },
    data: getUpdateGhostScoreInstructionDataDecoder().decode(instruction.data),
  };
}
